import { FC } from "react";
import { useTranslation } from "react-i18next";
import Card from "components/card/Card";
import {
  CpiDashboardData,
  EmissionsByMonthResponse,
  formatNumber,
} from "pages/cpiReports/types";
import { isTileLoading } from "../overviewData";
import { computeYoy } from "../emissionsYoy";
import KpiTile from "./KpiTile";
import Co2ByMonthChart from "./Co2ByMonthChart";

interface QueryLike<T> {
  data: T | null | undefined;
  isError: boolean;
  refetch: () => void;
}

interface EmissionsSectionProps {
  dashboard: QueryLike<CpiDashboardData>;
  byMonth: QueryLike<EmissionsByMonthResponse>;
}

const formatTons = (n: number | null | undefined): string =>
  typeof n === "number"
    ? n.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : "—";

const EmissionsSection: FC<EmissionsSectionProps> = ({ dashboard, byMonth }) => {
  const { t } = useTranslation();
  const d = dashboard.data;

  // Year-over-year from the monthly series (present only when the API returned
  // a previous-year comparison). A negligible prior-year baseline is treated as
  // "not comparable" rather than shown as an exploded percentage.
  const rows = byMonth.data?.data ?? [];
  const yoy = computeYoy(rows);

  // Indicative cost of the total WTW CO2e, already computed server-side as
  // totalEmissionWtwTons × co2PricePerTon (the frontend requests €45/t), so it
  // stays consistent with the CPI Reports page and follows the price if it
  // ever becomes configurable.
  const co2Cost = d?.indicativeCostEuros ?? null;

  return (
    <div className="overview-emis">
      <KpiTile
        label={t("dashboard.totalCo2Wtw")}
        loading={isTileLoading(dashboard)}
        error={dashboard.isError}
        onRetry={dashboard.refetch}
        value={
          <>
            {formatTons(d?.totalEmissionWtwTons)}
            <span className="kpi-tile__unit">t</span>
          </>
        }
        sub={
          // eslint-disable-next-line no-nested-ternary
          yoy.meaningful && yoy.pct != null ? (
            <span
              className={`kpi-yoy kpi-yoy--${yoy.pct > 0 ? "up" : "down"}`}
            >
              {yoy.pct > 0 ? "↑" : "↓"} {Math.abs(yoy.pct).toFixed(0)}%{" "}
              {t("dashboard.vsLastYear")}
            </span>
          ) : yoy.hasPrevious ? (
            <span className="kpi-yoy kpi-yoy--neutral">
              {t("dashboard.noPriorYear")}
            </span>
          ) : undefined
        }
      />

      <KpiTile
        label={t("dashboard.co2Cost")}
        loading={isTileLoading(dashboard)}
        error={dashboard.isError}
        onRetry={dashboard.refetch}
        value={
          <>
            &euro;{co2Cost != null ? formatNumber(co2Cost, 0) : "—"}
            <span className="kpi-tile__unit">@ €{d?.co2PricePerTon ?? 45}/t</span>
          </>
        }
      />

      <Card customClass="kpi-tile overview-chart-card">
        <div className="overview-chart-card__head">
          <span className="overview-chart-card__title">
            {t("dashboard.co2ByMonth")}
          </span>
          <span className="overview-chart-card__sub">{t("dashboard.tonnes")}</span>
        </div>
        <Co2ByMonthChart
          data={byMonth.data?.data}
          loading={isTileLoading(byMonth)}
          error={byMonth.isError}
          onRetry={byMonth.refetch}
        />
      </Card>
    </div>
  );
};

export default EmissionsSection;
